import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { FeedRecipient } from "@/utils/inviteForecast";

/**
 * The reply/failure rows for the Invitation tab's live feed: campaign_recipients
 * of the recent invite batches that have an outcome (joined / opted out) or
 * failed. The batch "Sent N" events are added client-side from useInviteBatches;
 * this hook supplies only the per-customer events. RLS scopes the read; the
 * feed is glanceable, so it never returns the full phone number to the UI —
 * maskPhone does that. Enabled only once we know the batch ids to scope to.
 */
export const useInviteLiveFeed = (campaignIds: string[]) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<FeedRecipient[]>({
    queryKey: ["sms-invite-live-feed", campaignIds],
    enabled: campaignIds.length > 0,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("campaign_recipients")
        .select("id, customer_phone, outcome, status, replied_at, sent_at, created_at")
        .in("campaign_id", campaignIds)
        .or("outcome.not.is.null,status.eq.failed")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as FeedRecipient[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};
